/*
 * <product-line-picker>  (mix & match, cross-product AJAX swap)
 * -------------------------------------------------------------
 * Picker for products that belong to a Product Line metaobject
 * (product.metafields.custom.product_line). The metaobject curates a flat,
 * ordered list of variants, possibly across several products. Each option is
 * server-rendered as an <a data-line-variant> carrying its variant / product /
 * price / availability / url in data-* attributes.
 *
 * Selection behaviour:
 *   - Option belongs to the CURRENT product  -> inline update (fast, no reload):
 *       hidden [data-product-select] input, price nodes, ATC availability, URL.
 *   - Option belongs to ANOTHER product      -> dynamic in-place swap: fetch that
 *       product through the Section Rendering API (?section_id=<id>&variant=<id>),
 *       replace the product section DOM, and re-init the theme's section framework
 *       (@shopify/theme-sections) so media gallery / form / dropdowns work again.
 *       On any failure it falls back to a normal full-page navigation.
 *   - No swappable section around the picker -> inline update, and nothing else.
 *       The Section Rendering API only renders sections of the requested URL's
 *       template, so a picker posed outside the product template (a landing page
 *       carrying a featured-product section, say) has nothing to swap. It stays
 *       put: selecting still points the form at the right variant, where the old
 *       fallback navigated the visitor away to the product page.
 *
 * theme.js's shopify:section:load handler only exists in the Theme Editor
 * (designMode), so on a live storefront we re-init by running the registered
 * product-template onLoad callbacks directly from window.Shopify.theme.sections,
 * unloading the old section first to run Flickity / listener cleanup.
 */
(function () {
  if (window.customElements && customElements.get('product-line-picker')) return;

  var SECTION_SELECTOR = '[data-section-type="product-template"]';
  var swapping = false;

  /* ---- theme-sections framework bridge ---------------------------------- */

  function sectionsManager() {
    return window.Shopify && window.Shopify.theme && window.Shopify.theme.sections;
  }

  // Run the old section instance's cleanup (Flickity destroy, resize listeners…)
  // and drop it from the registry, mirroring the framework's own unload().
  function unloadOldSection(node) {
    var mgr = sectionsManager();
    if (!mgr || !mgr.instances) return;
    for (var i = mgr.instances.length - 1; i >= 0; i--) {
      if (mgr.instances[i].container === node) {
        try {
          if (typeof mgr.instances[i].onUnload === 'function') mgr.instances[i].onUnload();
        } catch (e) {
          /* keep going */
        }
        mgr.instances.splice(i, 1);
      }
    }
  }

  // Re-instantiate all product-template components on the freshly injected node.
  // theme.js's shopify:section:load listener is only attached when designMode was
  // truthy at page load, so on a live storefront it does NOT exist — we can't rely
  // on the event. Instead we replicate what the framework's Section does: run each
  // registered onLoad callback with { id, type, container }, then push an
  // instance-like entry so a later swap can unload it (Flickity destroy, listeners).
  function loadNewSection(node) {
    var mgr = sectionsManager();
    if (!mgr || !mgr.registered) return;
    var type = node.getAttribute('data-section-type');
    var registration = mgr.registered[type];
    if (!registration) return;
    var stack =
      typeof registration.getStack === 'function'
        ? registration.getStack()
        : registration.callStack;
    if (!stack || !stack.onLoad) return;

    var props = { id: node.getAttribute('data-section-id'), type: type, container: node };
    stack.onLoad.forEach(function (fn) {
      try {
        fn.call(props);
      } catch (e) {
        /* one bad component shouldn't stop the others */
      }
    });

    if (Array.isArray(mgr.instances)) {
      mgr.instances.push({
        container: node,
        id: props.id,
        type: type,
        onUnload: function () {
          (stack.onUnload || []).forEach(function (fn) {
            try {
              fn.call(props);
            } catch (e) {
              /* no-op */
            }
          });
        },
      });
    }

    // Nudge sliders (Flickity) to recompute now that the node is laid out.
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        window.dispatchEvent(new Event('resize'));
      });
    }
  }

  // The sticky bar (snippet product-sticky.liquid) moves itself to <body> via an
  // inline script that does not re-run when injected as parsed HTML. Rebuild it.
  function reinjectSticky(doc) {
    try {
      var old = document.getElementById('ProductStickyBar');
      if (old) old.remove();
      var fresh = doc.getElementById('ProductStickyBar');
      if (!fresh) return;
      document.body.appendChild(document.importNode(fresh, true));
      var scripts = doc.querySelectorAll('script');
      for (var i = 0; i < scripts.length; i++) {
        var content = scripts[i].textContent || '';
        if (content.indexOf('ProductStickyBar') !== -1) {
          var runnable = document.createElement('script');
          runnable.textContent = content;
          document.body.appendChild(runnable);
        }
      }
    } catch (e) {
      /* sticky is non-critical; never let it break the swap */
    }
  }

  /* ---- the swap --------------------------------------------------------- */

  function swapProduct(handle, variantId, fallbackUrl, pushState, section) {
    if (swapping) return;
    // Deux sections produit peuvent cohabiter sur une même page : on échange
    // celle du picker, pas la première venue.
    var oldSection = section || document.querySelector(SECTION_SELECTOR);
    if (!oldSection || !handle) {
      window.location.assign(fallbackUrl);
      return;
    }
    swapping = true;
    oldSection.classList.add('product-line-picker-swapping');

    var sectionId = oldSection.getAttribute('data-section-id');
    var url =
      '/products/' +
      handle +
      '?section_id=' +
      encodeURIComponent(sectionId) +
      (variantId ? '&variant=' + encodeURIComponent(variantId) : '');

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Section request failed');
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var newSection = doc.querySelector(SECTION_SELECTOR);
        if (!newSection) throw new Error('No product section in response');

        var imported = document.importNode(newSection, true);
        unloadOldSection(oldSection);
        oldSection.replaceWith(imported); // custom elements (incl. this picker) re-upgrade
        loadNewSection(imported); // re-init form / media / dropdowns / swatches
        reinjectSticky(doc);

        if (pushState !== false) {
          var newUrl = '/products/' + handle + (variantId ? '?variant=' + variantId : '');
          window.history.pushState({ productLineSwap: true }, '', newUrl);
        }
      })
      .catch(function () {
        window.location.assign(fallbackUrl); // graceful degradation
      })
      .then(function () {
        swapping = false;
        var current = document.querySelector(SECTION_SELECTOR);
        if (current) current.classList.remove('product-line-picker-swapping');
      });
  }

  // Back / forward: re-swap to the product in the popped URL (no new history entry).
  if (!window.__productLinePopstateBound) {
    window.__productLinePopstateBound = true;
    window.addEventListener('popstate', function () {
      if (!document.querySelector('product-line-picker')) return;
      if (!document.querySelector(SECTION_SELECTOR)) return;
      var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
      if (!match) return;
      var variant = new URLSearchParams(window.location.search).get('variant');
      swapProduct(match[1], variant, window.location.href, false);
    });
  }

  /* ---- the element ------------------------------------------------------ */

  class ProductLinePicker extends HTMLElement {
    connectedCallback() {
      this.options = Array.from(this.querySelectorAll('[data-line-variant]'));
      if (!this.options.length) return;

      this.currentProductId = String(this.dataset.currentProduct || '');
      /*
       * Le swap passe par la Section Rendering API, qui ne rend que les sections
       * du template demandé. Hors template produit il n'y a donc rien à
       * échanger, et l'ancien fallback emmenait le visiteur sur la fiche produit
       * — sur une page d'atterrissage, ça se lit comme une redirection.
       */
      this.swappableSection = this.closest(SECTION_SELECTOR);
      this.scope = this.closest('[data-product]') || document;
      this.form =
        this.closest('[data-product-form]') ||
        this.scope.querySelector('[data-product-form]');
      this.soldOutText = this.dataset.soldOutText || '';

      this.options.forEach((el) => {
        el.addEventListener('change', () => {
          if (el.checked) this.onSelect(el);
        });
      });

      // Reflect the server-rendered current variant (no URL rewrite on load).
      var currentId = String(this.dataset.currentVariant || '');
      var active =
        this.options.find((el) => el.dataset.lineVariant === currentId) ||
        this.options.find((el) => el.dataset.available === 'true') ||
        this.options[0];
      this.selectInline(active, false, false);
    }

    onSelect(el) {
      var samePage = el.dataset.productId === this.currentProductId;
      if (samePage || !this.swappableSection) {
        // Sans section échangeable, l'URL du produit n'a pas à s'écrire non plus.
        this.selectInline(el, samePage && !!this.swappableSection, true);
      } else {
        swapProduct(
          el.dataset.productHandle,
          el.dataset.lineVariant,
          el.dataset.url,
          true,
          this.swappableSection
        );
      }
    }

    // Inline update for options belonging to the currently displayed product.
    selectInline(el, pushUrl, notify) {
      if (!el) return;
      el.checked = true; // native .radio__input:checked drives the selected visual

      var id = el.dataset.lineVariant;
      var available = el.dataset.available === 'true';

      this.scope
        .querySelectorAll('[data-product-form] input[name="id"], [data-product-select]')
        .forEach((input) => {
          input.value = id;
        });

      this.scope.querySelectorAll('[data-product-price]').forEach((node) => {
        node.textContent = el.dataset.price || '';
      });
      this.scope.querySelectorAll('[data-compare-price]').forEach((node) => {
        if (el.dataset.compare) {
          node.textContent = el.dataset.compare;
          node.classList.remove('hidden');
        } else {
          node.textContent = '';
          node.classList.add('hidden');
        }
      });

      this.updateAvailability(available);
      this.updateTitle(el);

      if (pushUrl && el.dataset.url) {
        try {
          window.history.replaceState({}, '', el.dataset.url);
        } catch (e) {
          /* no-op */
        }
      }

      if (notify) this.notifyVariantChange(el);
    }

    /*
     * Le picker pilote le formulaire à la main, sans passer par le ProductForm
     * du thème — seul émetteur de theme:variant:change. Les afficheurs branchés
     * dessus restaient donc sur le variant précédent : le prix remisé de l'offre
     * promo, notamment, qui est le seul visible quand l'offre est active, le
     * plein tarif étant masqué en face.
     */
    notifyVariantChange(el) {
      var cents = parseInt(el.dataset.priceCents, 10);
      if (isNaN(cents)) return;

      this.dispatchEvent(
        new CustomEvent('theme:variant:change', {
          detail: {
            variant: {
              id: el.dataset.lineVariant,
              price: cents,
              available: el.dataset.available === 'true'
            }
          },
          bubbles: true
        })
      );
    }

    /*
     * Une option peut appartenir à un autre produit que celui rendu par le
     * serveur : le titre affiché n'est alors plus le sien. On le suit, lien
     * compris, faute de quoi la section annonce un produit et en vend un autre.
     */
    updateTitle(el) {
      var title = el.dataset.title;
      if (!title) return;

      this.scope.querySelectorAll('[data-product-title]').forEach(function (node) {
        var link = node.querySelector('[data-product-title-link]');
        var target = link || node;
        target.textContent = title;

        if (link) {
          link.title = title;
          if (el.dataset.url) link.setAttribute('href', el.dataset.url);
        }
      });
    }

    updateAvailability(available) {
      if (!this.form) return;
      var button = this.form.querySelector('button[name="add"]');
      if (!button) return;

      var label = button.querySelector('[data-add-to-cart-text]');
      if (available) {
        button.removeAttribute('disabled');
        if (label && this._addToCartText) label.textContent = this._addToCartText;
      } else {
        if (label) {
          if (!this._addToCartText) this._addToCartText = label.textContent.trim();
          if (this.soldOutText) label.textContent = this.soldOutText;
        }
        button.setAttribute('disabled', 'disabled');
      }
    }
  }

  customElements.define('product-line-picker', ProductLinePicker);
})();
